export interface DataGridInputs {
  customHeaders?: any[]
  showActionsColumn?: boolean
  showFiltering?: boolean
  showSearchRow?: boolean
  showPaging?: boolean
  customPageSize?: number
  customPageSizes?: number[]
  customNoDataText?: string
  customAriaLabel?: string
  showQueueActions?: boolean
  disableApplicationClick?: boolean
  /** When true, educator_name uses default cell rendering (plain text), not the link template. */
  plainTextEducatorName?: boolean
  customActionTemplate?: string
  showNewDocumentIcon?: boolean
  showNewUploadIcon?: boolean
  showStaffUpdateIcon?: boolean
  showPaymentCompletedIcon?: boolean
  showPaymentPendingIcon?: boolean
  // showNewApplicantUpdateIcon?: boolean
  data: any[] | null
  visibleColumns?: { [key: string]: boolean } | null
  role: string
  activeTab: string
  hidePager?: boolean
  _changeKey?: any
  url?: string
  fillEmptyRows?: boolean
  minVisibleRows?: number
  initialFilters?: any[]
  /** CMS keys G0029 / G0030 / G0032 (and overrides) for App ID icon tooltips */
  guidanceTooltips?: Record<string, string>
}

export class DataGridVO {
  customHeaders: any[]
  showActionsColumn: boolean
  showFiltering: boolean
  showSearchRow: boolean
  showPaging: boolean
  customPageSize: number
  customPageSizes: number[]
  customNoDataText: string
  customAriaLabel: string
  showQueueActions: boolean
  disableApplicationClick: boolean
  plainTextEducatorName: boolean
  customActionTemplate: string
  showNewDocumentIcon: boolean
  showNewUploadIcon: boolean
  showStaffUpdateIcon: boolean
  showPaymentCompletedIcon: boolean
  showPaymentPendingIcon: boolean
  // showNewApplicantUpdateIcon: boolean
  data: any[] | null
  visibleColumns: { [key: string]: boolean } | null
  role: string
  activeTab: string
  hidePager?: boolean
  _changeKey?: any
  url?: string
  fillEmptyRows: boolean
  minVisibleRows: number
  initialFilters?: any[]
  guidanceTooltips: Record<string, string>

  private static readonly DEFAULTS: DataGridInputs = {
    customHeaders: [],
    showActionsColumn: true,
    showFiltering: true,
    showSearchRow: true,
    showPaging: true,
    customPageSize: 10,
    customPageSizes: [2,5, 10, 15, 25, 50],
    customNoDataText: 'No records',
    customAriaLabel: 'Data Grid',
    showQueueActions: true,
    disableApplicationClick: false,
    plainTextEducatorName: false,
    customActionTemplate: 'customActions',
    showNewDocumentIcon: false,
    showNewUploadIcon: false,
    showStaffUpdateIcon: false,
    showPaymentCompletedIcon: false,
    showPaymentPendingIcon: false,
    // showNewApplicantUpdateIcon: false,
    data: null,
    visibleColumns: null,
    role: '',
    activeTab: '',
    hidePager: false,
    url: '',
    fillEmptyRows: false,
    minVisibleRows: 0,
    initialFilters: undefined,
    guidanceTooltips: {},
  };

  constructor(config?: Partial<DataGridInputs>) {
    const merged = { ...DataGridVO.DEFAULTS, ...config } as Required<DataGridInputs>;
    this.customHeaders = merged.customHeaders;
    this.showActionsColumn = merged.showActionsColumn;
    this.showFiltering = merged.showFiltering;
    this.showSearchRow = merged.showSearchRow;
    this.showPaging = merged.showPaging;
    this.customPageSize = merged.customPageSize;
    this.customPageSizes = merged.customPageSizes;
    this.customNoDataText = merged.customNoDataText;
    this.customAriaLabel = merged.customAriaLabel;
    this.showQueueActions = merged.showQueueActions;
    this.disableApplicationClick = merged.disableApplicationClick;
    this.plainTextEducatorName = merged.plainTextEducatorName;
    this.customActionTemplate = merged.customActionTemplate;
    this.showStaffUpdateIcon = merged.showStaffUpdateIcon;
    this.showPaymentCompletedIcon = merged.showPaymentCompletedIcon;
    this.showPaymentPendingIcon = merged.showPaymentPendingIcon;
    // this.showNewApplicantUpdateIcon = merged.showNewApplicantUpdateIcon;
    this.showNewDocumentIcon = merged.showNewDocumentIcon;
    this.showNewUploadIcon = merged.showNewUploadIcon;
    this.data = merged.data;
    this.visibleColumns = merged.visibleColumns;
    this.role = merged.role;
    this.activeTab = merged.activeTab;
    this.hidePager = merged.hidePager;
    this._changeKey = merged._changeKey;
    this.url = merged.url;
    this.fillEmptyRows = merged.fillEmptyRows;
    this.minVisibleRows = merged.minVisibleRows;
    this.initialFilters = merged.initialFilters;
    this.guidanceTooltips = merged.guidanceTooltips ?? {};
  }

  static create(config?: Partial<DataGridInputs>): DataGridVO {
    return new DataGridVO(config);
  }

  updateConfig(config: Partial<DataGridInputs>): DataGridVO {
    return new DataGridVO({ ...this, ...config });
  }

  isValid(): boolean {
    return !!(this.role && this.activeTab);
  }

  toConfig(): DataGridInputs {
    return {
      customHeaders: this.customHeaders,
      showActionsColumn: this.showActionsColumn,
      showFiltering: this.showFiltering,
      showSearchRow: this.showSearchRow,
      showPaging: this.showPaging,
      customPageSize: this.customPageSize,
      customPageSizes: this.customPageSizes,
      customNoDataText: this.customNoDataText,
      customAriaLabel: this.customAriaLabel,
      showQueueActions: this.showQueueActions,
      disableApplicationClick: this.disableApplicationClick,
      plainTextEducatorName: this.plainTextEducatorName,
      customActionTemplate: this.customActionTemplate,
      showStaffUpdateIcon: this.showStaffUpdateIcon,
      showPaymentCompletedIcon: this.showPaymentCompletedIcon,
      showPaymentPendingIcon: this.showPaymentPendingIcon,
      // showNewApplicantUpdateIcon: this.showNewApplicantUpdateIcon,
      showNewDocumentIcon: this.showNewDocumentIcon,
      showNewUploadIcon: this.showNewUploadIcon,
      data: this.data,
      visibleColumns: this.visibleColumns,
      role: this.role,
      activeTab: this.activeTab,
      hidePager: this.hidePager,
      _changeKey: this._changeKey,
      url: this.url,
      fillEmptyRows: this.fillEmptyRows,
      minVisibleRows: this.minVisibleRows,
      initialFilters: this.initialFilters,
      guidanceTooltips: this.guidanceTooltips,
    };
  }
} 
export const FIXED_EST_TZ = 'Etc/GMT+5'; // Fixed UTC-5 (EST only; no DST)